import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import CounterpartySidebar from './CounterpartySidebar';
import CounterpartyHeader from './CounterpartyHeader';
import TransferWidget from './TransferWidget';
import WithdrawRequestWidget from './WithdrawRequestWidget';
import { TransactionsFilterProvider } from '../context/TransactionsFilterContext';
import { DashboardFilterProvider } from '../context/DashboardFilterContext';
import { clearAuthToken, cpApi, getAuthToken } from '../api/api';
import type { Counterparty, CounterpartyTeam } from '../types';
import { aggregateTeamBalances, pickCounterpartyBalanceSlice } from '../utils/salaryHoldDisplay';

interface Props {
  children: React.ReactNode;
}

export default function CounterpartyLayout({ children }: Props) {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState<Counterparty | null>(null);
  const [team, setTeam] = useState<CounterpartyTeam | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const navigate = useNavigate();

  const isAirexRequestsPage = location.pathname === '/requests';
  const isTeamPage = location.pathname === '/team';
  const isTransactionsPage = location.pathname === '/transactions';
  const isDashboardPage = location.pathname === '/dashboard';

  const [statusFilter, setStatusFilter] = useState('all');
  const [teamSearchQuery, setTeamSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState<{ from: Date | null; to: Date | null }>(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    yesterday.setHours(0, 0, 0, 0);
    return { from: yesterday, to: today };
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [showTransfer, setShowTransfer] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);

  const isAuthError = (err: unknown): boolean => {
    const status = (err as { response?: { status?: number } })?.response?.status;
    return status === 401 || status === 403;
  };

  useEffect(() => {
    // Ставим дефолт только когда оба пусты — иначе перезаписываем промежуточный выбор (from выбран, to ещё нет)
    if (isAirexRequestsPage && !dateRange.from && !dateRange.to) {
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      yesterday.setHours(0, 0, 0, 0);
      setDateRange({ from: yesterday, to: today });
    }
  }, [isAirexRequestsPage, dateRange.from, dateRange.to]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoadError(null);
        const token = getAuthToken();
        if (!token) {
          navigate('/login');
          return;
        }

        const profile = await cpApi.getProfile();
        if (profile.is_active === false) {
          clearAuthToken();
          navigate('/login');
          return;
        }
        setUser(profile);

        // Команда не должна валить вход в кабинет: если эндпоинт недоступен/временно упал,
        // продолжаем с team=null и даем пользователю работать.
        try {
          const myTeam = await cpApi.getMyTeam();
          setTeam(myTeam);
        } catch (teamErr) {
          if (isAuthError(teamErr)) {
            clearAuthToken();
            navigate('/login');
            return;
          }
          setTeam(null);
          window.dispatchEvent(new CustomEvent('showToast', {
            detail: { message: 'Команда временно недоступна, работаем в упрощенном режиме', type: 'error' }
          }));
        }
      } catch (err) {
        if (isAuthError(err)) {
          clearAuthToken();
          navigate('/login');
          return;
        }
        setLoadError('Не удалось загрузить профиль. Проверьте интернет и попробуйте снова.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  const isResponsible = !!(team && user && team.responsible_counterparty_id === user.id);
  const myMember = user ? team?.members?.find((m) => m.counterparty_id === user.id) : undefined;
  const personalSlice = user ? pickCounterpartyBalanceSlice(user, myMember) : null;
  const teamTotals = team?.members?.length
    ? aggregateTeamBalances(team.members)
    : null;
  const sidebarBalance = isResponsible && teamTotals
    ? { ...teamTotals, isTeam: true as const }
    : personalSlice
      ? { ...personalSlice, isTeam: false as const }
      : null;
  const withdrawAvailable = personalSlice?.available ?? 0;

  useEffect(() => {
    const h = () => setShowTransfer(true);
    window.addEventListener('openTransferWidget', h);
    return () => window.removeEventListener('openTransferWidget', h);
  }, []);

  useEffect(() => {
    // На мобиле после навигации закрываем drawer, чтобы контент не перекрывался.
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleStatusFilterChange = (status: string) => setStatusFilter(status);
  const handleDateRangeChange = (range: { from: Date | null; to: Date | null }) => setDateRange(range);
  const handleSearchChange = (query: string) => setSearchQuery(query);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (loadError && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 p-4">
        <div className="max-w-md w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 text-center">
          <p className="text-sm text-gray-700 dark:text-gray-200 mb-4">{loadError}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium"
          >
            Повторить
          </button>
        </div>
      </div>
    );
  }

  if (!user) return null;

  const isSettingsPage = location.pathname === '/settings';
  if (!user.twofa_check && !isSettingsPage) {
    navigate('/settings', { replace: true });
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="h-dvh min-h-0 flex overflow-hidden bg-gray-50 dark:bg-gray-950">
      <CounterpartySidebar
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen((o) => !o)}
        user={user}
        team={team}
        balanceSummary={sidebarBalance}
        isResponsible={isResponsible}
      />
      <TransactionsFilterProvider team={team} isResponsible={isResponsible} user={user}>
      <DashboardFilterProvider>
      <div className="flex-1 flex flex-col min-w-0 min-h-0 bg-gray-50 dark:bg-gray-950">
        <CounterpartyHeader
          onMenuToggle={() => setSidebarOpen((o) => !o)}
          isRequestsPage={isAirexRequestsPage}
          isSettingsPage={location.pathname === '/settings'}
          showAirexRequestsFilters={isAirexRequestsPage}
          showTeamSearch={isTeamPage}
          showTransactionsFilters={isTransactionsPage}
          showDashboardDateFilter={isDashboardPage}
          teamSearchQuery={teamSearchQuery}
          onTeamSearchChange={(q) => {
            setTeamSearchQuery(q);
            window.dispatchEvent(new CustomEvent('teamSearchChange', { detail: { search: q } }));
          }}
          showAddTeamMemberButton={isTeamPage && !!team && !!user && team.responsible_counterparty_id === user.id}
          onAddTeamMember={() => window.dispatchEvent(new CustomEvent('openAddTeamMemberWidget'))}
          showNewTransactionButton={isTeamPage && !!team && !!user && team.responsible_counterparty_id === user.id && (team.members?.length ?? 0) > 1}
          onNewTransaction={() => window.dispatchEvent(new CustomEvent('openTransferWidget'))}
          showWithdrawButton={isAirexRequestsPage && !!team}
          onWithdraw={() => setShowWithdraw(true)}
          statusFilter={statusFilter}
          onStatusFilterChange={handleStatusFilterChange}
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          onRefresh={() => window.dispatchEvent(new CustomEvent('refreshRequests'))}
        />
        <main className="flex-1 overflow-y-auto w-full min-w-0 min-h-0 pb-2 md:pb-0">{children}</main>
        {showTransfer && team && (
          <TransferWidget
            team={team}
            onClose={() => setShowTransfer(false)}
            onSuccess={() => {
              setShowTransfer(false);
              window.dispatchEvent(new CustomEvent('refreshTransactions'));
            }}
          />
        )}
        {showWithdraw && personalSlice && (
          <WithdrawRequestWidget
            availableBalance={withdrawAvailable}
            heldBalance={personalSlice.held}
            nextSalaryHoldReleaseAt={personalSlice.nextReleaseAt}
            onClose={() => setShowWithdraw(false)}
            onSuccess={() => {
              setShowWithdraw(false);
              window.dispatchEvent(new CustomEvent('refreshRequests'));
            }}
          />
        )}
      </div>
      </DashboardFilterProvider>
      </TransactionsFilterProvider>
    </div>
  );
}
